import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { LocateFixed, MapPin } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { anunciosMetaApi, km, mensagemDeErro } from "./api";

/**
 * Área do anúncio: o pino da loja e o círculo do raio, num mapa (Leaflet, o
 * mesmo do mapa de calor). O pino já vem do endereço de Configurações →
 * Empresa (o servidor marca ao conectar); dá para arrastar ou clicar no mapa.
 */
const CENTRO_BR = [-15.8, -47.9];
const AZUL_META = "#0866FF";
const RAIO_MAX_BARRA = 20;
const PINO = L.divIcon({
  className: "",
  html: `<div style="width:18px;height:18px;border-radius:50%;background:${AZUL_META};border:3px solid #fff;box-shadow:0 0 0 2px ${AZUL_META}"></div>`,
  iconSize: [18, 18], iconAnchor: [9, 9],
});

export default function LocalizacaoRaio({ conexao, endereco, podeGerir, onSalvo }) {
  const divRef = useRef(null);
  const mapaRef = useRef(null);
  const pinoRef = useRef(null);
  const circuloRef = useRef(null);
  const raioSalvo = Number(conexao?.raio_km || 4);
  const salvo = conexao?.latitude != null ? [conexao.latitude, conexao.longitude] : null;
  const [ponto, setPonto] = useState(salvo);
  const [raio, setRaio] = useState(raioSalvo);
  const [ocupado, setOcupado] = useState(false);
  const mudou = Boolean(ponto) && (!salvo || ponto[0] !== salvo[0] || ponto[1] !== salvo[1] || raio !== raioSalvo);

  useEffect(() => {
    if (!divRef.current || mapaRef.current) return undefined;
    const mapa = L.map(divRef.current, { zoomControl: true, scrollWheelZoom: false }).setView(CENTRO_BR, 4);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { attribution: "© OpenStreetMap" }).addTo(mapa);
    mapa.on("click", (e) => podeGerir && setPonto([e.latlng.lat, e.latlng.lng]));
    mapaRef.current = mapa;
    // O Leaflet mede o contêiner uma vez só; se a largura mudar depois, sobra uma faixa cinza.
    const medidor = new ResizeObserver(() => {
      mapa.invalidateSize();
      if (circuloRef.current) mapa.fitBounds(circuloRef.current.getBounds(), { padding: [24, 24] });
    });
    medidor.observe(divRef.current);
    return () => { medidor.disconnect(); mapa.remove(); mapaRef.current = null; pinoRef.current = null; circuloRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const mapa = mapaRef.current;
    if (!mapa || !ponto) return;
    if (!pinoRef.current) {
      pinoRef.current = L.marker(ponto, { draggable: !!podeGerir, icon: PINO }).addTo(mapa);
      pinoRef.current.on("dragend", (e) => { const p = e.target.getLatLng(); setPonto([p.lat, p.lng]); });
    } else pinoRef.current.setLatLng(ponto);
    if (!circuloRef.current) circuloRef.current = L.circle(ponto, { radius: raio * 1000, color: AZUL_META, weight: 2, fillOpacity: 0.1 }).addTo(mapa);
    else { circuloRef.current.setLatLng(ponto); circuloRef.current.setRadius(raio * 1000); }
    // O mapa sempre mostra o círculo inteiro: é o alcance do anúncio.
    mapa.fitBounds(circuloRef.current.getBounds(), { padding: [24, 24] });
  }, [ponto, raio, podeGerir]);

  const usarEndereco = async () => {
    setOcupado(true);
    try { const r = await anunciosMetaApi.geocodificar(); setPonto([r.latitude, r.longitude]); }
    catch (e) { toast.error(mensagemDeErro(e, "Não achei o endereço. Clique no mapa onde a loja fica.")); }
    finally { setOcupado(false); }
  };

  const salvar = async () => {
    setOcupado(true);
    try {
      const r = await anunciosMetaApi.salvarLocalizacao({ latitude: ponto[0], longitude: ponto[1], raio_km: raio });
      toast.success("Área do anúncio salva.");
      onSalvo?.(r.conexao);
    } catch (e) { toast.error(mensagemDeErro(e, "Não foi possível salvar.")); }
    finally { setOcupado(false); }
  };

  return (
    <section className="rounded-xl border bg-card p-4 sm:p-5 space-y-3" data-e2e="meta-localizacao" data-tem-ponto={ponto ? "1" : "0"}>
      <div>
        <h2 className="font-bold">Área do anúncio</h2>
        <p className="text-xs text-muted-foreground">O anúncio aparece para quem está dentro do círculo. Raio maior alcança mais gente e pede mais investimento.</p>
      </div>
      <p className="flex items-start gap-1.5 text-sm">
        <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" />
        {endereco
          ? <span>Loja: <b data-e2e="meta-endereco">{endereco}</b>{podeGerir && <span className="text-muted-foreground"> · arraste o pino se ele não estiver no lugar certo</span>}</span>
          : <span className="text-muted-foreground">Sem endereço em Configurações → Empresa. Clique no mapa onde a loja fica.</span>}
      </p>
      <div ref={divRef} className="h-80 w-full rounded-lg overflow-hidden border" style={{ zIndex: 1 }} />
      <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
        <label className="flex items-center gap-3 text-sm flex-1 min-w-[16rem]">
          <span className="text-muted-foreground shrink-0">Raio</span>
          <input type="range" min={1} max={Math.max(RAIO_MAX_BARRA, raio)} step={0.5} value={raio} disabled={!podeGerir}
            onChange={(e) => setRaio(Number(e.target.value))} className="flex-1 accent-primary" data-e2e="meta-raio" />
          <span className="font-semibold tabular-nums w-14 text-right" data-e2e="meta-raio-valor">{km(raio)}</span>
        </label>
        {podeGerir && (
          <div className="flex flex-wrap gap-2">
            <button onClick={usarEndereco} disabled={ocupado}
              className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm hover:border-primary disabled:opacity-50">
              <LocateFixed className="w-3.5 h-3.5" /> Voltar ao endereço da loja
            </button>
            <button onClick={salvar} disabled={ocupado || !mudou} data-e2e="meta-salvar-localizacao"
              className="rounded-lg bg-primary text-primary-foreground px-4 py-1.5 text-sm font-semibold disabled:opacity-40">
              {mudou ? "Salvar área" : "Área salva"}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
